import React, { useContext, useEffect } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { withRouter } from 'react-router'

import { fetchTourV2Offer } from 'actions/tour/TourV2Actions'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import Error500Page from 'components/Pages/Error500Page'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import findCheapestTourV2PurchasableOption from 'lib/tours/findCheapestTourV2PurchasableOption'
import GeoContext from 'contexts/geoContext'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import { tourOfferClicked } from 'analytics/eventDefinitions'
import useQueryParams from 'hooks/useQueryParams'
import useEuropeOfferPageWalledGarden from 'hooks/OfferPage/useEuropeOfferPagWalledGarden'

interface Props extends RouteComponentProps<{ offerId: string }> {
  fetching: boolean
  offerError?: any
  tourV2Offer: App.Tours.TourV2Offer
  currency: string
}

function TourV2OfferPage(props: Props) {
  const {
    fetching,
    match,
    offerError,
    tourV2Offer,
    currency,
  } = props
  const geo = useContext(GeoContext)
  const dispatch = useAppDispatch()
  const privateRequestKey = useQueryParams().get('privateRequestKey') ?? undefined

  useEuropeOfferPageWalledGarden()

  useEffect(() => {
    // Always attempt to fetch if we don't have the offer and we're not currently loading
    if (!tourV2Offer && !fetching) {
      dispatch(fetchTourV2Offer(match.params.offerId, privateRequestKey))
    }
  }, [dispatch, match.params.offerId, privateRequestKey, tourV2Offer, fetching])

  useEffect(() => {
    if (tourV2Offer) {
      fireEcommerceGTMEvent(tourOfferClicked(tourV2Offer, currency))
    }
  }, [currency, tourV2Offer])

  if (offerError) {
    if (offerError.status === 404) {
      return <Error404PageWithOffers />
    } else {
      return <Error500Page error={offerError} />
    }
  }

  if (fetching || !tourV2Offer) {
    return <LoadingIndicator />
  }

  if (tourV2Offer) {
    const cheapestPurchasableOption = findCheapestTourV2PurchasableOption(tourV2Offer)
    const variationId = cheapestPurchasableOption?.fkVariationId ?? Object.keys(tourV2Offer.variations)[0]
    const toUrl = `/${geo.currentRegionCode.toLowerCase()}/tour/${tourV2Offer.id}/${variationId}`
    return <RedirectWithStatus code={301} to={toUrl} />
  }

  return null
}

function mapStateToProps(appState: App.State, ownProps: Partial<Props>) {
  const { offerId } = ownProps.match!.params
  const query = ownProps.location!.search

  const privateRequestKey = new URLSearchParams(query).get('privateRequestKey')
  const offerKey = privateRequestKey ? `${offerId}-${privateRequestKey}` : offerId

  const tourV2Offer = appState.offer.tourV2Offers[offerKey]

  return {
    fetching: appState.offer.offersLoading[offerKey],
    offerError: appState.offer.offerErrors[offerKey],
    currency: appState.geo.currentCurrency,
    tourV2Offer,
  }
}

export default withRouter(connect(mapStateToProps)(TourV2OfferPage))
